/**
 * Farben und Nivo-Thema der Sektion „Anfos & Umsatz". Werte aus der Referenzpalette des dataviz-Skills
 * (blau = Reihe 1 und Größen, orange = Reihe 2), je Modus eigene Stufen, geprüft mit
 * `validate_palette.js` gegen den hellen (#fcfcfc) und dunklen Seitengrund. Text trägt nie die
 * Reihenfarbe; Gitter und Achsen sind haarfein und zurückhaltend.
 *
 * Nicht `theme.palette.primary.main` als Diagrammfarbe nehmen — das kippt im Dunkelmodus auf fast
 * schwarz (`.claude/rules/react-frontend.md` § Charts).
 */
import { FC, ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import type { BarCustomLayerProps, BarDatum, ComputedBarDatum } from '@nivo/bar';

/**
 * Kodierung „unsicher" (Audit D6, 30.10.2026): laufender Zeitraum, junge Anfos, „Rechnung kommt noch" — DIESELBE Farbe
 * wie die sichere Marke, geringe Deckkraft plus Kontur in der vollen Farbe. Volles Grau (`muted`) bleibt Kontext
 * (vorläufig, beendet, Vergleichsgröße). Keine Schraffur: Textur ist im dataviz-Skill der Kanal für Barrierefreiheit/Druck,
 * nie Standard. Die Kontur trägt die Unterscheidung auch ohne Farbsehen.
 */
export const UNSICHER = { deckkraft: 0.3, kontur: 1.5 } as const;

/** `#rrggbb` → `rgba(r,g,b,a)`; andere Formate unverändert (dann ohne Deckkraft). */
export function mitDeckkraft(farbe: string, a: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(farbe.trim());
  if (!m) return farbe;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}

/** Schrift auf einer Fläche dieser Farbe: dunkel auf hellen, weiß auf kräftigen Flächen (relative Luminanz > 0,4). */
export function textAuf(farbe: string): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(farbe.trim());
  if (!m) return '#0b0b0b';
  const lin = (h: string) => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * lin(m[1]) + 0.7152 * lin(m[2]) + 0.0722 * lin(m[3]);
  return l > 0.4 ? '#0b0b0b' : '#ffffff';
}

/** Füllung einer unsicheren Marke in ihrer Farbe. */
export const unsicherFuellung = (farbe: string): string => mitDeckkraft(farbe, UNSICHER.deckkraft);

/**
 * Nivo-Balkenebene: Kontur um jede unsichere Marke (nach 'bars' einhängen; die Füllung liefert `colors` mit
 * `unsicherFuellung`). `farbe` gibt die VOLLE Farbe der Marke, `null` = sicher. Breite/Höhe nie negativ (D9).
 * Jede Kontur trägt `data-unsicher` — die Browser-Abnahme zählt sie im DOM.
 */
export const unsicherKonturen =
  <D extends BarDatum = BarDatum>(farbe: (bar: ComputedBarDatum<D>) => string | null) =>
  function UnsicherKonturen({ bars }: BarCustomLayerProps<D>) {
    const w = UNSICHER.kontur;
    return (
      <g>
        {bars.map((b) => {
          const f = farbe(b);
          if (!f || b.width <= w || b.height <= w) return null;
          return (
            <rect
              key={`unsicher-${b.key}`}
              x={b.x + w / 2}
              y={b.y + w / 2}
              width={Math.max(0, b.width - w)}
              height={Math.max(0, b.height - w)}
              rx={1.5}
              fill="none"
              stroke={f}
              strokeWidth={w}
              pointerEvents="none"
              data-unsicher="kontur"
            />
          );
        })}
      </g>
    );
  };

export interface VizStyle {
  dark: boolean;
  /** Reihe 1 (blau) — Hauptgröße, einzelne Reihe. */
  series1: string;
  /** Reihe 2 (orange) — zweite Reihe in Stapeln. */
  series2: string;
  /** Hellere Stufe von Reihe 1 — „roh" neben „geschrumpft", zweite Ausprägung derselben Größe. */
  series1Soft: string;
  /** Kontext (vorläufig, beendet, Vergleichsgröße). Unreife/laufende Zeiträume tragen `UNSICHER`, nicht Grau. */
  muted: string;
  /**
   * Geordnete Stufen (eine Farbe, hell → kräftig), z. B. Rechnungsstufen „keine Rechnung" → „bezahlt".
   * Geprüft mit `validate_palette.js --ordinal` in beiden Modi (08.10.2026); im Dunkelmodus ist die
   * kräftigste Stufe die hellste.
   */
  stufen: [string, string, string, string, string];
  /**
   * Feste Farbe je Kanalgruppe (Kapitel „Kanäle & Werbeflächen") — folgt der Gruppe, nie dem Rang: wer eine
   * Gruppe abwählt, färbt die übrigen nicht um. Slots 1–6 der Referenzpalette in fester Reihenfolge, „Sonstige"
   * grau. Geprüft 20.10.2026 mit `validate_palette.js` (Slots 1–6 als Stapelreihenfolge): hell CVD ΔE 9,1,
   * Normalsicht 19,6; dunkel 8,4 / 19,3. Hell liegen Aqua, Gelb, Magenta unter 3:1 → Legende + Tabelle
   * daneben sind Pflicht (Relief-Regel).
   */
  kanalFarben: Record<'webinar' | 'direct_mailing' | 'newsletter' | 'doi' | 'ads' | 'welcome' | 'other', string>;
  /**
   * Rechnungsstufen (Audit D8): bezahlt in Blau (kräftig = „bezahlt", heller = „nur Testphase"), verloren in Orange
   * („Rechnung, nichts gezahlt", „vor der 1. Rechnung beendet" kräftig); „Rechnung kommt noch" = Blau mit `UNSICHER`.
   * Je Gruppe eine Farbe in zwei Stufen, geprüft 30.10.2026 mit `validate_palette.js --ordinal` (blau hell 184f95/5598e7,
   * dunkel 184f95/3987e5; orange hell eb6834/a8461c, dunkel a8461c/f08a5d — alle PASS) und die Nahtstelle Blau↔Orange
   * kategorial (hell ΔE 15,7 normal / CVD 13,8; dunkel ≥ 26). Hell liegt 5598e7 knapp unter 3:1 → Legende + Tabelle.
   */
  rechnungsstufen: { bezahlt: string; nurTestphase: string; rechnungOffen: string; vorErsterRechnung: string; kommtNoch: string };
  /** Schrift der Seite — auch für Nivo-Text und eigene `<text>`-Ebenen (D3; Nivo setzt sonst „sans-serif"). */
  fontFamily: string;
  inkPrimary: string;
  inkSecondary: string;
  grid: string;
  /** Spur hinter HTML-Balken (Spannen, Rangbalken) — zurückgenommen, im Dunkelmodus eigene Stufe. */
  spur: string;
  baseline: string;
  /** Nivo-Thema (Achsen, Gitter, Legenden, Tooltip). */
  nivo: Record<string, unknown>;
}

export const useVizStyle = (): VizStyle => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const fontFamily = String(theme.typography.fontFamily ?? 'sans-serif');
  const s = dark
    ? {
        series1: '#3987e5',
        series2: '#d95926',
        series1Soft: '#184f95',
        muted: '#898781',
        stufen: ['#184f95', '#256abf', '#3987e5', '#6da7ec', '#b7d3f6'] as [string, string, string, string, string],
        kanalFarben: {
          webinar: '#3987e5',
          direct_mailing: '#d95926',
          newsletter: '#199e70',
          doi: '#c98500',
          ads: '#d55181',
          welcome: '#008300',
          other: '#898781',
        },
        rechnungsstufen: { bezahlt: '#3987e5', nurTestphase: '#184f95', rechnungOffen: '#a8461c', vorErsterRechnung: '#f08a5d', kommtNoch: '#3987e5' },
        inkPrimary: '#ffffff',
        inkSecondary: '#c3c2b7',
        grid: 'rgba(255,255,255,0.10)',
        spur: 'rgba(255,255,255,0.08)',
        baseline: 'rgba(255,255,255,0.25)',
        tooltipBg: '#1f2a40',
      }
    : {
        series1: '#2a78d6',
        series2: '#eb6834',
        series1Soft: '#86b6ef',
        muted: '#898781',
        stufen: ['#86b6ef', '#5598e7', '#256abf', '#184f95', '#0d366b'] as [string, string, string, string, string],
        kanalFarben: {
          webinar: '#2a78d6',
          direct_mailing: '#eb6834',
          newsletter: '#1baf7a',
          doi: '#eda100',
          ads: '#e87ba4',
          welcome: '#008300',
          other: '#898781',
        },
        rechnungsstufen: { bezahlt: '#184f95', nurTestphase: '#5598e7', rechnungOffen: '#eb6834', vorErsterRechnung: '#a8461c', kommtNoch: '#2a78d6' },
        inkPrimary: '#0b0b0b',
        inkSecondary: '#52514e',
        grid: '#e1e0d9',
        spur: '#f1f0ec',
        baseline: '#c3c2b7',
        tooltipBg: '#ffffff',
      };
  const text = (fill: string) => ({ fill, fontSize: 12, fontFamily });
  return {
    dark,
    ...s,
    fontFamily,
    nivo: {
      text: text(s.inkSecondary),
      axis: {
        domain: { line: { stroke: s.baseline, strokeWidth: 1 } },
        ticks: { line: { stroke: s.baseline, strokeWidth: 1 }, text: text(s.inkSecondary) },
        legend: { text: text(s.inkSecondary) },
      },
      grid: { line: { stroke: s.grid, strokeWidth: 1 } },
      legends: { text: text(s.inkSecondary) },
      labels: { text: text(s.inkPrimary) },
      markers: { text: text(s.inkSecondary) },
      tooltip: {
        container: {
          background: s.tooltipBg,
          color: s.inkPrimary,
          fontFamily,
          fontSize: 12,
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        },
      },
      crosshair: { line: { stroke: s.inkSecondary, strokeWidth: 1, strokeOpacity: 0.6 } },
    },
  };
};

/** Tooltip-Zeile: Wert stark, Bezeichnung zurückgenommen, Reihe als kurzer Strich in ihrer Farbe. */
export const TipRow: FC<{ color?: string; label: ReactNode; value: ReactNode }> = ({ color, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap', py: 0.25 }}>
    {color && <Box sx={{ width: 12, height: 2, borderRadius: 1, backgroundColor: color, flexShrink: 0 }} />}
    <Box component="span" sx={{ fontWeight: 700 }}>
      {value}
    </Box>
    <Box component="span" sx={{ opacity: 0.8 }}>
      {label}
    </Box>
  </Box>
);

/**
 * Legende in HTML über dem Diagramm: Farbfeld wie die Marke (Rechteck für Balken, Strich für Linien). `unsicher`:
 * Farbfeld mit geringer Deckkraft und Kontur wie die Marke (`UNSICHER`), `gestrichelt` für Linien.
 */
export const Legende: FC<{ items: { label: string; color: string; shape?: 'rect' | 'line'; unsicher?: boolean; gestrichelt?: boolean }[] }> = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.75, mb: 1 }}>
    {items.map((i) => (
      <Box key={i.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13 }} data-legende-unsicher={i.unsicher ? '1' : undefined}>
        <Box
          sx={{
            width: i.shape === 'line' && i.gestrichelt ? 16 : 12,
            height: i.shape === 'line' ? (i.gestrichelt ? 0 : 2) : 12,
            borderRadius: i.shape === 'line' ? 1 : '3px',
            flexShrink: 0,
            boxSizing: 'border-box',
            ...(i.shape === 'line' && i.gestrichelt
              ? { borderTop: `2px dashed ${i.color}` }
              : i.unsicher
                ? { backgroundColor: unsicherFuellung(i.color), border: `${UNSICHER.kontur}px solid ${i.color}` }
                : { backgroundColor: i.color }),
          }}
        />
        {i.label}
      </Box>
    ))}
  </Box>
);

/** Achsenbeschriftung deutsch: `2,5 %`, `1.000 €` — ganze Zahlen ohne Nachkommastelle. */
export const achse =
  (einheit = '') =>
  (v: string | number | Date): string => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    const text = n.toLocaleString('de-DE', { maximumFractionDigits: Number.isInteger(n) ? 0 : 1 });
    return einheit ? `${text} ${einheit}` : text;
  };

/** Rechnungsstufen der PL-X-Anfos (K2-Grafik, Verlust-Bilanz). */
export type RechnungsstufeKey = 'paid' | 'trial_only' | 'invoiced_unpaid' | 'no_invoice_running' | 'no_invoice_ended';

/**
 * Stapelreihenfolge von der Grundlinie (Audit D8): erst bezahlt (Blau), dann verloren (Orange), oben das Offene
 * („Rechnung kommt noch", `UNSICHER`). Die Legende folgt genau dieser Reihenfolge.
 */
export const RECHNUNGSSTUFEN_STAPEL: readonly RechnungsstufeKey[] = ['paid', 'trial_only', 'invoiced_unpaid', 'no_invoice_ended', 'no_invoice_running'];

/** Farbe einer Rechnungsstufe und ob sie `UNSICHER` gezeichnet wird. */
export const rechnungsstufeFarbe = (viz: Pick<VizStyle, 'rechnungsstufen'>, k: RechnungsstufeKey): { farbe: string; unsicher: boolean } => {
  const r = viz.rechnungsstufen;
  switch (k) {
    case 'paid':
      return { farbe: r.bezahlt, unsicher: false };
    case 'trial_only':
      return { farbe: r.nurTestphase, unsicher: false };
    case 'invoiced_unpaid':
      return { farbe: r.rechnungOffen, unsicher: false };
    case 'no_invoice_ended':
      return { farbe: r.vorErsterRechnung, unsicher: false };
    default:
      return { farbe: r.kommtNoch, unsicher: true };
  }
};

/**
 * Gemeinsame Skala für kleine Vielfache (Audit D1): runder Höchstwert und die Ticks dazu — Achse UND Gitter bekommen
 * dasselbe Feld, sonst zeichnet Nivo das Gitter nach eigenen Ticks. Schritt 1/2/2,5/5 × 10^n, höchstens `anzahl`
 * Abschnitte; gewählt wird der kleinste runde Höchstwert (46 % → 50 %, nicht 60 %).
 */
export function runderHoechstwert(max: number, anzahl = 3): { max: number; ticks: number[] } {
  if (!Number.isFinite(max) || max <= 0) return { max: 1, ticks: [0, 1] };
  const zehner = 10 ** Math.floor(Math.log10(max / anzahl));
  let best: { schritt: number; n: number } | null = null;
  for (const f of [1, 2, 2.5, 5, 10, 20]) {
    const schritt = f * zehner;
    const n = Math.max(1, Math.ceil(max / schritt - 1e-9));
    if (n > anzahl) continue;
    if (!best || n * schritt < best.n * best.schritt - 1e-9) best = { schritt, n };
  }
  const { schritt, n } = best ?? { schritt: 20 * zehner, n: 1 };
  const ticks = Array.from({ length: n + 1 }, (_, i) => Number((i * schritt).toPrecision(12)));
  return { max: ticks[ticks.length - 1], ticks };
}
